/**
 * CFO ROI model for HART. Four inputs, live modeled recovery, and the gated
 * executive one-pager. Lifts its result up so the demo form can attach the
 * numbers to the lead.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CircleDollarSign, Loader2, FileDown, TrendingUp, Clock, Percent, Gauge } from "lucide-react";
import { API } from "@/App";
import HartOnePagerDialog from "./HartOnePagerDialog";

const DEFAULTS = { annual_spend: 48000000, facilities: 6, procedure_rooms: 42, current_rebate_pct: 1.5 };

const SLIDERS = [
  { k: "annual_spend", label: "Annual med/surg supply spend", min: 2000000, max: 750000000,
    step: 1000000, money: true },
  { k: "facilities", label: "Facilities in the system", min: 1, max: 60, step: 1 },
  { k: "procedure_rooms", label: "OR / procedure rooms", min: 0, max: 400, step: 1 },
  { k: "current_rebate_pct", label: "Rebate you capture today", min: 0, max: 6, step: 0.1, pct: true },
];

const usd = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const compact = (n) =>
  n >= 1000000 ? `$${(n / 1000000).toFixed(n >= 10000000 ? 0 : 1)}M` : usd(n);

export const HartRoiCalculator = ({ onResult }) => {
  const [inputs, setInputs] = useState(DEFAULTS);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openGate, setOpenGate] = useState(false);
  const timer = useRef(null);

  const run = useCallback(async (payload) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/hart/roi/estimate`, payload);
      setRes(data);
      onResult?.(data);
    } catch {
      /* model is illustrative; a transient failure just leaves the last figures up */
    } finally {
      setBusy(false);
    }
  }, [onResult]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(inputs), 220);
    return () => clearTimeout(timer.current);
  }, [inputs, run]);

  const set = (k, v) => setInputs((p) => ({ ...p, [k]: v }));

  const bigs = res ? [
    { icon: TrendingUp, k: "Modeled annual benefit", v: compact(res.total_benefit), tone: "navy" },
    { icon: Gauge, k: "Net of platform cost", v: compact(res.net_benefit), tone: "navy" },
    { icon: Clock, k: "Payback", v: `${res.payback_months.toFixed(1)} mo`, tone: "amber" },
    { icon: Percent, k: "Effective capture", v: `${res.effective_rebate_pct.toFixed(2)}%`, tone: "navy" },
  ] : [];

  return (
    <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-6" data-testid="hart-roi-calculator">
      {/* ----------------------------- inputs ----------------------------- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#0056A8] mb-4">
          Your numbers
        </p>
        <div className="space-y-5">
          {SLIDERS.map((s) => (
            <div key={s.k}>
              <div className="flex items-baseline justify-between mb-1.5">
                <label className="text-[13px] font-semibold text-slate-600">{s.label}</label>
                <span className="text-[15px] font-black text-[#0a1c3c] tabular-nums"
                  data-testid={`roi-value-${s.k}`}>
                  {s.money ? compact(inputs[s.k]) : s.pct ? `${inputs[s.k].toFixed(1)}%` : inputs[s.k]}
                </span>
              </div>
              <input type="range" min={s.min} max={s.max} step={s.step} value={inputs[s.k]}
                onChange={(e) => set(s.k, Number(e.target.value))}
                data-testid={`roi-input-${s.k}`}
                className="w-full h-1.5 appearance-none rounded-full bg-slate-200 accent-[#0056A8]
                  cursor-pointer [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#0056A8]
                  [&::-webkit-slider-thumb]:shadow-md" />
            </div>
          ))}
        </div>
        {res && (
          <p className="mt-5 text-[11px] leading-relaxed text-slate-500" data-testid="roi-basis">
            Rebate-bearing spend {compact(res.rebate_bearing_spend)} · captured today{" "}
            {compact(res.captured_today)} · modeled platform cost {compact(res.platform_fee)}/yr.
          </p>
        )}
      </div>

      {/* ----------------------------- results ----------------------------- */}
      <div className="rounded-2xl border border-[#003366]/15 bg-gradient-to-br from-[#f3f8fd] to-white
        p-6 relative overflow-hidden">
        {busy && (
          <Loader2 className="absolute right-5 top-5 w-4 h-4 animate-spin text-[#0056A8]"
            data-testid="roi-busy" />
        )}
        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#0056A8] mb-4">
          What recovery is worth
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {bigs.map((b, i) => (
            <motion.div key={b.k} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 * i }}
              className={`rounded-xl border p-3.5 ${b.tone === "amber"
                ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}
              data-testid={`roi-metric-${i}`}>
              <b.icon className={`w-4 h-4 mb-2 ${b.tone === "amber" ? "text-amber-600" : "text-[#0056A8]"}`} />
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 leading-tight">
                {b.k}
              </p>
              <p className={`mt-1 text-xl md:text-2xl font-black tabular-nums tracking-tight
                ${b.tone === "amber" ? "text-amber-700" : "text-[#003366]"}`}>{b.v}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-5 divide-y divide-slate-200 border-y border-slate-200">
          {(res?.lines || []).map((l) => (
            <div key={l.key} className="flex items-start justify-between gap-4 py-2.5"
              data-testid={`roi-line-${l.key}`}>
              <div>
                <p className="text-[13.5px] font-semibold text-[#0a1c3c] leading-snug">{l.label}</p>
                <p className="text-[11.5px] text-slate-500 leading-snug">{l.note}</p>
              </div>
              <p className="text-[15px] font-black text-[#003366] tabular-nums whitespace-nowrap">
                {usd(l.value)}
              </p>
            </div>
          ))}
        </div>

        <button type="button" onClick={() => setOpenGate(true)} disabled={!res}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl
            bg-[#0056A8] hover:bg-[#003366] disabled:opacity-50 px-6 py-4 text-sm font-bold
            uppercase tracking-wider text-white transition-colors shadow-lg shadow-blue-900/20"
          data-testid="roi-onepager-cta">
          <FileDown className="w-4 h-4" /> Email me the executive one-pager
        </button>
        <p className="mt-2.5 text-[11px] text-slate-500 text-center">
          <CircleDollarSign className="inline w-3 h-3 mr-1 -mt-0.5 text-slate-400" />
          {res?.disclaimer || "Illustrative model built on typical health-system contract structures."}
        </p>
      </div>

      <HartOnePagerDialog open={openGate} onOpenChange={setOpenGate} roiInputs={inputs} />
    </div>
  );
};

export default HartRoiCalculator;
